import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiInfo } from 'react-icons/fi';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import axios from 'axios';
import api from '../../api';
import './FAQs.css';

const Faqs = () => {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(api.getFAQs);
      if (response.data.success && response.data.faqs) {
        setFaqs(response.data.faqs);
      } else {
        setError('Failed to load FAQs - invalid response format');
      }
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError(`Failed to load FAQs: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleFAQ = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // Group FAQs by category
  const groupedFAQs = faqs.reduce((acc, faq) => {
    const category = faq.category || 'general';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(faq);
    return acc;
  }, {});

  const categoryTitles = {
    general: 'General',
    courses: 'Courses',
    payment: 'Payment & Pricing',
    certificates: 'Certificates',
    instructor: 'Instructors',
    account: 'Account & Profile',
    technical: 'Technical Support'
  };

  // Define category display order: General first, then others alphabetically
  const categoryOrder = ['general', 'account', 'certificates', 'courses', 'instructor', 'payment', 'technical'];
  
  // Sort categories: General first, then others in defined order
  const sortedCategories = Object.keys(groupedFAQs).sort((a, b) => {
    const indexA = categoryOrder.indexOf(a);
    const indexB = categoryOrder.indexOf(b);
    // If both are in the order array, use their index
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    // If only one is in the array, prioritize it
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    // If neither is in the array, sort alphabetically
    return a.localeCompare(b);
  });

  return (
    <div className="faqs-container">
      {/* Info icon button to navigate home */}
      <button className="info-icon-button" onClick={() => navigate('/')}>
        ×
      </button>

      <h1>Frequently Asked Questions</h1>

      {loading && (
        <div className="faqs-loading">
          <p>Loading FAQs...</p>
        </div>
      )}

      {error && (
        <div className="faqs-error">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && faqs.length === 0 && (
        <div className="faqs-empty">
          <p>No FAQs available at the moment. Please check back later.</p>
        </div>
      )}

      {!loading && !error && faqs.length > 0 && (
        <div className="faqs-content">
          {sortedCategories.map((category) => (
            <div key={category} className="faq-category">
              <h2 className="faq-category-title">{categoryTitles[category] || category}</h2>
              {groupedFAQs[category].map((faq, index) => (
                <div 
                  key={faq._id} 
                  className={`faq-item ${expandedIndex === `${category}-${index}` ? 'expanded' : ''}`}
                >
                  <div 
                    className="faq-question" 
                    onClick={() => toggleFAQ(`${category}-${index}`)}
                  >
                    <h3>{faq.question}</h3>
                    <span className="faq-toggle">
                      {expandedIndex === `${category}-${index}` ? (
                        <FaChevronUp />
                      ) : (
                        <FaChevronDown />
                      )}
                    </span>
                  </div>
                  {expandedIndex === `${category}-${index}` && (
                    <div className="faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Faqs;
